/**
 * Sculk shrieker patches for the deep dark (vanilla: 1-2 summoning shriekers per sculk patch, a catalyst nearby).
 * Runs after caveDecor in features.ts `decorate`, so the deep-dark caves already carry their sculk floor: this only
 * swaps a few of those floor blocks, in a fraction of chunks, for naturally generated (can_summon) shriekers and a
 * catalyst. Pure per-chunk (hash-seeded), so it reads the same on any thread.
 */

import { AIR, SCULK_CATALYST_ID, SCULK_ID, SCULK_SHRIEKER_SUMMON_ID } from '../blocks';
import { CHUNK_SIZE } from '../constants';
import { hash2, mulberry32 } from '../noise';
import { type GenCtx, vy } from './ctx';

/** Share of chunks with sculk floor that get a shrieker patch. */
const PATCH_CHANCE = 0.45;
/** Deep dark only (the same depth caveDecor uses for the biome). */
const MAX_Y = vy(-10);

export function sculkShriekers(ctx: GenCtx): void {
  if (hash2(ctx.cx, ctx.cz, ctx.seed ^ 0x5c1c) >= PATCH_CHANCE) return;
  const out = ctx.out;
  // open sculk floor with room to stand: air above and above that
  const spots: number[] = [];
  for (let y = 2; y < MAX_Y; y++) {
    for (let z = 1; z < CHUNK_SIZE - 1; z++) {
      for (let x = 1; x < CHUNK_SIZE - 1; x++) {
        const i = x + z * CHUNK_SIZE + y * 256;
        if (out[i - 256] === SCULK_ID && out[i] === AIR && out[i + 256] === AIR) spots.push(i - 256);
      }
    }
  }
  if (spots.length < 6) return;
  const rnd = mulberry32((ctx.seed ^ Math.imul(ctx.cx, 0x1f3d5b79) ^ Math.imul(ctx.cz, 0x2c1b3c6d)) >>> 0);
  const pick = (): number => spots.splice(Math.floor(rnd() * spots.length), 1)[0];
  const shriekers = 1 + (rnd() < 0.5 ? 1 : 0);
  for (let k = 0; k < shriekers; k++) out[pick()] = SCULK_SHRIEKER_SUMMON_ID;
  out[pick()] = SCULK_CATALYST_ID;
}
